import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { modalStyles } from '../styles/modalStyles';
import PlansCard from './PlansCard';
import { Plan, fetchPlans, getMySubscription } from '../services/companyService';
import { setMySubscriptionData, type MySubscriptionStoredData } from '../helpers/storageUtils';
import { STORAGE_KEYS } from '../helpers/constants';
import { getFetchErrorMessage } from '../helpers/errorUtils';

interface PlansModalProps {
  open: boolean;
  onClose: () => void;
  onPlanSelect?: (planId: string) => void;
  currentPlanId?: string;
  /** When provided, "Current Plan" badge uses planCode (preferred over currentPlanId) */
  currentPlanCode?: string;
  /** Fallback match against catalog plan.name when code/id are missing (case-insensitive in PlansCard) */
  currentPlanName?: string;
  title?: string;
  subtitle?: string;
  plans?: Plan[]; // If provided, won't fetch plans internally
  autoFetch?: boolean; // Whether to fetch plans automatically (default: true)
  showCurrentPlanBadge?: boolean;
  gridColumns?: { xs: number; md: number; lg: number };
  subscriptionId?: string; // Optional subscription ID for cancel subscription
  platformName?: string; // Optional platform name (defaults to 'prism7')
  /** After mySubscription is saved (cancel/reactivate); use to refetch invoices / usage (e.g. fetchCompanySubscriptionDetails) */
  onAfterMySubscriptionSynced?: () => void | Promise<void>;
}

const PlansModal: React.FC<PlansModalProps> = ({
  open,
  onClose,
  onPlanSelect,
  currentPlanId,
  currentPlanCode,
  currentPlanName,
  title = 'Manage Plan',
  subtitle = 'Choose a plan that better fits your needs',
  plans: externalPlans,
  autoFetch = true,
  showCurrentPlanBadge = true,
  gridColumns = { xs: 1, md: 2, lg: 3 },
  subscriptionId,
  platformName,
  onAfterMySubscriptionSynced,
}) => {
  const [plans, setPlans] = useState<Plan[]>(externalPlans || []);
  const [loading, setLoading] = useState<boolean>(autoFetch && !externalPlans);
  const [error, setError] = useState<string | null>(null);

  // Fetch plans if autoFetch is enabled and no external plans provided
  useEffect(() => {
    if (open && autoFetch && !externalPlans) {
      const loadPlans = async () => {
        try {
          setLoading(true);
          setError(null);
          
          const plansResponse = await fetchPlans();
          if (plansResponse.success) {
            setPlans(plansResponse.data);
          } else {
            throw new Error(plansResponse.message || 'Failed to fetch plans');
          }
        } catch (err) {
          console.error('Error fetching plans:', err);
          setError(getFetchErrorMessage(err, 'Failed to fetch plans'));
        } finally {
          setLoading(false);
        }
      };

      loadPlans();
    } else if (externalPlans) {
      // Use external plans if provided
      setPlans(externalPlans);
      setLoading(false);
      setError(null);
    }
  }, [open, autoFetch, externalPlans]);

  const handleClose = () => {
    onClose();
  };

  // After cancel or reactivate: refresh mySubscription, optional parent refetch (Zoho company details), then close
  const refreshSubscriptionAndClose = async () => {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      try {
        const subscriptionResponse = await getMySubscription(token);
        if (subscriptionResponse?.success && subscriptionResponse?.data) {
          setMySubscriptionData({ ...subscriptionResponse.data } as MySubscriptionStoredData);
          window.dispatchEvent(new CustomEvent('subscription-updated'));
        }
      } catch (e) {
        console.warn('Failed to refresh subscription after plan change:', e);
      }
    }
    try {
      await onAfterMySubscriptionSynced?.();
    } catch (e) {
      console.warn('onAfterMySubscriptionSynced failed:', e);
    }
    onClose();
  };

  return (
    <Dialog
      id="plans-modal"
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleClose();
        }
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          ...modalStyles.dialog['& .MuiDialog-paper'],
        },
      }}
    >
      <DialogTitle id="plans-modal-title" sx={modalStyles.dialogTitle}>
        <Box>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          id="plans-modal-close-btn"
          onClick={handleClose}
          sx={modalStyles.closeButton}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        id="plans-modal-content"
        sx={{
          padding: '16px 24px', // Set padding to 16px 24px as requested
          marginBottom: '10px', // Add margin-bottom: 10px as requested
          overflow: 'auto',
          maxHeight: '70vh',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.2)'
                : 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.3)'
                  : 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        {loading ? (
          <Box
            id="plans-modal-loading"
            sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
          >
            <CircularProgress id="plans-modal-loading-spinner" />
          </Box>
        ) : error ? (
          <Alert id="plans-modal-error" severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <PlansCard
            plans={plans}
            currentPlanId={currentPlanId}
            currentPlanCode={currentPlanCode}
            currentPlanName={currentPlanName}
            onPlanSelect={onPlanSelect}
            showCurrentPlanBadge={showCurrentPlanBadge}
            gridColumns={gridColumns}
            subscriptionId={subscriptionId}
            platformName={platformName}
            onCancelSuccess={refreshSubscriptionAndClose}
            onReactivateSuccess={refreshSubscriptionAndClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlansModal;

